import { Stack, Box, useToast, Card } from 'braid-design-system';
import { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';

import { ContractStatus } from 'src/generated/graphql';
import {
  type ClientListingExpireUpdate,
  useExpireListing,
} from 'src/hooks/useExpireListing';
import type { StoreProps } from 'src/store';
import type {
  ClientListingForListingsTab,
  ListingProductForListingsTab,
} from 'src/types/graphql-derived';

import AdAlertNotificationExpired from './components/AdAlertNotificationExpired/AdAlertNotificationExpired';
import AdAlertNotificationExpiring from './components/AdAlertNotificationExpiring/AdAlertNotificationExpiring';
import AdMessage from './components/AdMessage/AdMessage';
import Details from './components/Details/Details';
import ExpireListingDialog from './components/ExpireListingDialog/ExpireListingDialog';
import Title from './components/Title/Title';

interface RowProps {
  listing: ClientListingForListingsTab;
  listingProducts: ListingProductForListingsTab[];
  onListingExpire: (id: number, listing: ClientListingExpireUpdate) => void;
  hasActiveListings: boolean;
}

const Row = ({
  listing,
  listingProducts,
  onListingExpire,
  hasActiveListings,
}: RowProps) => {
  const hasPaid = useSelector((store: StoreProps) => store.account.paid);
  const showToast = useToast();
  const [isRefreshingListing, setIsRefreshingListing] = useState(false);
  const [isOpen, showDialog] = useState(false);
  const expireListing = useExpireListing(listing);

  const handleExpireListingClicked = async () => {
    showDialog(true);
  };

  const handleExpireListingConfirmed = async (
    reason: string,
    comments: string,
  ) => {
    showDialog(false);
    const result = await expireListing.expire(reason, comments);

    if (result.kind === 'success') {
      setIsRefreshingListing(true);
      await onListingExpire(listing.id, result.listing);
      setIsRefreshingListing(false);

      showToast({
        tone: 'positive',
        message: 'You removed your ad',
        data: { testid: 'expire-listing-success-toast' },
      });
    } else {
      showToast({
        tone: 'critical',
        message: result.message,
        data: { testid: 'expire-listing-error-toast' },
      });
    }
  };

  const isProcessing =
    isRefreshingListing || expireListing.status.kind === 'loading';

  let errorMessage: string | undefined;

  return (
    <Fragment>
      <Box
        component="article"
        aria-label={listing.title}
        data={{ testid: 'dashboard-listing-row' }}
      >
        <Stack space="xsmall">
          {listing.baseContract?.status === ContractStatus.Active && (
            <AdAlertNotificationExpiring listing={listing} />
          )}

          {listing.baseContract?.status === ContractStatus.Expired && (
            <AdAlertNotificationExpired
              listing={listing}
              hasActiveListings={hasActiveListings}
              hasPaidContract={hasPaid}
            />
          )}

          {!isProcessing && (
            <AdMessage
              listing={listing}
              accountPaid={hasPaid}
              errorMessage={errorMessage}
            />
          )}

          <Card>
            <Stack space={{ mobile: 'xlarge', tablet: 'medium' }}>
              <Title listing={listing} />
              <Details
                listing={listing}
                expireFunction={handleExpireListingClicked}
                isProcessing={isProcessing}
                listingProducts={listingProducts}
              />
            </Stack>
          </Card>
        </Stack>
      </Box>

      <ExpireListingDialog
        isOpen={isOpen}
        closeDialog={() => showDialog(false)}
        handleExpireListingConfirmed={handleExpireListingConfirmed}
      />
    </Fragment>
  );
};

export default Row;
